import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  CalendarIcon,
  CakeIcon,
  GiftIcon,
  BuildingOfficeIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import calendarApi from '../../services/calendarApi';

const UpcomingEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchUpcomingEvents();
  }, []);

  const fetchUpcomingEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await calendarApi.getUpcomingEvents();
      setEvents(response.data.events || []);
    } catch (err) {
      console.error('Error fetching upcoming events:', err);
      setError('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Invalid date';
    
    try {
      // Parse the date string directly to avoid timezone conversion
      const [year, month, day] = dateString.split('-');
      
      if (!year || !month || !day) {
        return 'Invalid date';
      }
      
      // Create date object using individual components to avoid timezone issues
      const eventDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      // Reset time components for accurate comparison
      today.setHours(0, 0, 0, 0);
      tomorrow.setHours(0, 0, 0, 0);
      eventDate.setHours(0, 0, 0, 0);
      
      if (eventDate.getTime() === today.getTime()) {
        return 'Today';
      } else if (eventDate.getTime() === tomorrow.getTime()) {
        return 'Tomorrow';
      } else {
        // Format manually to avoid toLocaleDateString timezone issues
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                           'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        
        return `${dayNames[eventDate.getDay()]} ${monthNames[eventDate.getMonth()]} ${eventDate.getDate()}`;
      }
    } catch (e) {
      return 'Invalid date';
    }
  };

  const getEventIcon = (type) => {
    switch (type) {
      case 'birthday':
        return <CakeIcon className="h-5 w-5 text-pink-500" />;
      case 'work_anniversary':
        return <GiftIcon className="h-5 w-5 text-blue-500" />;
      case 'holiday':
        return <BuildingOfficeIcon className="h-5 w-5 text-green-500" />;
      default:
        return <CalendarIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getEventColor = (type) => {
    switch (type) {
      case 'birthday':
        return 'bg-pink-100 border-pink-300';
      case 'work_anniversary':
        return 'bg-blue-100 border-blue-300';
      case 'holiday':
        return 'bg-green-100 border-green-300';
      default:
        return 'bg-gray-100 border-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <CalendarIcon className="h-5 w-5 mr-2 text-blue-600" />
          Upcoming Events
        </h3>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="flex items-center space-x-3">
                <div className="h-8 w-8 bg-gray-200 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-1"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <CalendarIcon className="h-5 w-5 mr-2 text-blue-600" />
          Upcoming Events
        </h3>
        <div className="text-center py-4">
          <p className="text-red-500 text-sm">{error}</p>
          <button 
            onClick={fetchUpcomingEvents}
            className="mt-2 text-xs text-blue-600 hover:text-blue-500"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <CalendarIcon className="h-5 w-5 mr-2 text-blue-600" />
          Hey Heads Up!
        </h3>
        <Link
          to="/company/calendar"
          className="text-sm text-blue-600 hover:text-blue-500 flex items-center"
        >
          View All
          <ChevronRightIcon className="h-4 w-4 ml-1" />
        </Link>
      </div>

      {events.length === 0 ? (
        <div className="text-center py-6">
          <CalendarIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No upcoming events in the next 7 days</p>
          <Link
            to="/company/calendar"
            className="mt-2 text-xs text-blue-600 hover:text-blue-500"
          >
            View full calendar
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {events.slice(0, 5).map((event, index) => (
            <div
              key={event.id}
              className={`p-3 rounded-lg border ${getEventColor(event.type)}`}
            >
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-0.5">
                  {getEventIcon(event.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-black truncate">
                    {event.title}
                  </p>
                  <p className="text-xs text-black mt-1">
                    {formatDate(event.date)}
                  </p>
                  {event.description && (
                    <p className="text-xs text-black mt-1 truncate">
                      {event.description}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
          
          {events.length > 5 && (
            <div className="text-center pt-2">
              <Link
                to="/company/calendar"
                className="text-xs text-blue-600 hover:text-blue-500"
              >
                +{events.length - 5} more events
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UpcomingEvents; 